/** @jsxImportSource @emotion/react */

import React, { FC, useCallback, useMemo, useState } from 'react'
import { FieldArray } from 'react-final-form-arrays'
import { useForm, useFormState } from 'react-final-form'
import { useLazyQuery, useQuery } from '@apollo/client'
import { debounce } from 'lodash'
import Typography from '@mui/material/Typography'
import TabsNavigation from 'components/TabsNavigation'
import { TabsItem } from 'types/tabsItem'
import {
  findAllForCMSTask,
  findAllForCMSTask_findAllForCMSTask_items as Task,
  findAllForCMSTaskVariables,
} from '__generatedTypes__/findAllForCMSTask'
import { FIND_ALL_FOR_CMS_TASK, FIND_ONE_TASK } from 'queries/customSchedule'
import { DEBOUNCE_DELAY, SEARCH_ITEMS_COUNT } from 'constants/consts'
import { SearchTypeEnum, UpdateTaskInput } from '__generatedTypes__/globalTypes'
import { FormValues } from 'pagesNew/StudySchedule/Schedule/consts'
import { findOneTask, findOneTaskVariables } from '__generatedTypes__/findOneTask'
import { InputField } from 'components/Input'
import { TaskModal } from '../TaskModal/TaskModal'
import { useTaskModal } from '../TaskModal/hooks'
import { PhasePicker } from './PhasePicker'
import { TasksList } from './TasksList'
import * as classes from './styles'
import { PHASES } from './consts'

type Tab = {
  id: PHASES
  title: string
}

const TABS: Array<Tab> = [
  {
    id: PHASES.PHASE1,
    title: 'Phase 1',
  },
  {
    id: PHASES.PHASE2,
    title: 'Phase 2',
  },
  {
    id: PHASES.PHASE3,
    title: 'Phase 3',
  },
]

type Props = {
  platformId: string
}

export const ScheduleOrder: FC<Props> = ({ platformId }) => {
  const [search, setSearch] = useState('')

  const form = useForm()

  const { values } = useFormState<FormValues>()

  const [activeTab, setActiveTab] = useState<TabsItem>(TABS[0])

  const excludeIds = useMemo(() => {
    const ids = Object.values(PHASES).reduce((acc, phase) => {
      const value = values[phase] as Task[]

      return [...acc, ...(value || []).map(({ id }) => id)]
    }, [] as string[])

    return ids
  }, [values])

  const { data, loading } = useQuery<findAllForCMSTask, findAllForCMSTaskVariables>(FIND_ALL_FOR_CMS_TASK, {
    variables: {
      query: {
        take: SEARCH_ITEMS_COUNT,
        where: {
          platform: {
            id: platformId,
          },
          excludeIds: excludeIds?.length ? excludeIds : undefined,
        },
        search: search ? { query: search, type: SearchTypeEnum.ILike } : undefined,
      },
    },
    fetchPolicy: 'network-only',
  })

  const [findOneTask] = useLazyQuery<findOneTask, findOneTaskVariables>(FIND_ONE_TASK)

  const handleSearchTaskInputChange = useCallback(
    debounce((searchString: string) => {
      setSearch(searchString)
    }, DEBOUNCE_DELAY),
    []
  )

  const {
    isOpen: isTaskModalOpen,
    initialValues: taskModalInitialValues,
    onOpen: onOpenTaskModal,
    onClose: onCloseTaskModal,
    onSubmit: onSubmitTask,
    onDelete: onDeleteTask,
  } = useTaskModal(platformId, async (task?: Partial<UpdateTaskInput>) => {
    if (task?.id) {
      const { data } = await findOneTask({
        variables: {
          id: task.id,
        },
        fetchPolicy: 'network-only',
      })

      const updatedTask = data?.findOneTask

      if (!updatedTask) {
        return
      }

      form.change(
        activeTab.id,
        values[(activeTab as Tab).id]?.map((task) => (task.id === updatedTask.id ? { ...task, ...updatedTask } : task))
      )
    }
  })

  const handleChangeTab = (activeTab: TabsItem) => setActiveTab(activeTab)

  const handleEditTaskClick = (task: Task) => {
    onOpenTaskModal(task)
  }

  const items = data?.findAllForCMSTask.items || []

  return (
    <div>
      <Typography variant="h3" css={classes.title}>
        Schedule Order
      </Typography>
      <TabsNavigation css={classes.tabs} tabs={TABS} value={activeTab} onChange={handleChangeTab} />

      {Object.values(PHASES).map(
        (phase) =>
          phase === activeTab.id && (
            <React.Fragment key={phase}>
              <InputField
                fullWidth
                name={`${phase}Title`}
                css={classes.tooltipDescription}
                placeholder="Tooltip description (optional)"
                multiline
                label="Tooltip Description"
              />
              <FieldArray<Task> name={phase}>
                {({ fields, meta }) => (
                  <>
                    <PhasePicker
                      items={items}
                      label="Search Tasks"
                      value={fields.value ?? []}
                      onChange={(tasks) => form.change(phase, tasks)}
                      onInputChange={handleSearchTaskInputChange}
                      error={meta.touched && meta.error}
                      loading={loading}
                      formHelperText={meta.touched && typeof meta.error === 'string' ? meta.error : undefined}
                      hideSelectedItems
                      fullWidth
                      excludeIds={excludeIds}
                    />

                    <TasksList
                      tasks={fields.value ?? []}
                      onEdit={handleEditTaskClick}
                      onRemove={fields.remove}
                      onSort={(tasks) => form.change(phase, tasks)}
                    />
                  </>
                )}
              </FieldArray>
            </React.Fragment>
          )
      )}

      <TaskModal
        isOpen={isTaskModalOpen}
        initialValues={taskModalInitialValues}
        platformId={platformId}
        onClose={onCloseTaskModal}
        onSubmit={onSubmitTask}
        onDelete={onDeleteTask}
      />
    </div>
  )
}
